// react
import type { ReactNode } from 'react';
// libraries
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

// material ui
import { Autocomplete, Box, TextField } from '@mui/material';
import type { AutocompleteProps } from '@mui/material';

// relative
import RHFFieldLabel from './RHFFieldLabel';

export type RHFAutocompleteProps<TFieldValues extends FieldValues, TOption> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  options: TOption[];
  getOptionLabel: (option: TOption) => string;
  isOptionEqualToValue?: (option: TOption, value: TOption) => boolean;
  label?: ReactNode;
  required?: boolean;
  rules?: UseControllerProps<TFieldValues>['rules'];
  multiple?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
} & Omit<
  AutocompleteProps<TOption, boolean, false, false>,
  | 'renderInput'
  | 'options'
  | 'value'
  | 'onChange'
  | 'multiple'
  | 'getOptionLabel'
  | 'isOptionEqualToValue'
>;

export default function RHFAutocomplete<
  TFieldValues extends FieldValues,
  TOption,
>({
  name,
  control,
  options,
  getOptionLabel,
  isOptionEqualToValue,
  label,
  required,
  rules,
  multiple = false,
  helperText,
  placeholder,
  ...autocompleteProps
}: RHFAutocompleteProps<TFieldValues, TOption>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <Autocomplete
          {...autocompleteProps}
          multiple={multiple}
          options={options}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={isOptionEqualToValue}
          value={field.value ?? (multiple ? [] : null)}
          onChange={(_event, newValue) => {
            field.onChange(newValue ?? (multiple ? [] : null));
          }}
          onBlur={field.onBlur}
          renderInput={(params) => (
            <Box>
              {label ? (
                <RHFFieldLabel htmlFor={params.id} required={required}>
                  {label}
                </RHFFieldLabel>
              ) : null}
              <TextField
                {...params}
                required={required}
                placeholder={placeholder}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? helperText}
              />
            </Box>
          )}
        />
      )}
    />
  );
}
